import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check'

/**
 * The MINIMAL Firebase surface: app initialisation, callable functions and
 * App Check. Deliberately imports neither Auth nor Firestore.
 *
 * Why this file exists separately from config.ts: the public booking widget is
 * the highest-traffic, most latency-sensitive surface in the product, and it
 * only ever calls Cloud Functions. Importing the Firestore and Auth SDKs there
 * would add roughly 450 kB of JavaScript that a diner never uses. The build
 * splits these into separate chunks (see vite.config.ts) so the booking page
 * downloads only what it needs.
 *
 * On the Web API key: it is a public client identifier, not a credential, and
 * is designed to ship in the bundle. What actually protects the project is
 * Google Cloud API key restrictions, App Check, Authorised Domains, Firestore
 * Security Rules, and server-side authorisation. See docs/SECURITY.md.
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

const missing = Object.entries(firebaseConfig)
  .filter(([key, value]) => key !== 'measurementId' && !value)
  .map(([key]) => key)

if (missing.length > 0) {
  // Fail loudly at boot rather than with a cryptic SDK error later.
  throw new Error(
    `Firebase configuration is incomplete. Missing: ${missing.join(', ')}. ` +
      'Copy .env.example to .env and fill in the VITE_FIREBASE_* values.',
  )
}

export const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true'
export const FUNCTIONS_REGION = import.meta.env.VITE_FUNCTIONS_REGION || 'europe-west2'

export const firebaseApp: FirebaseApp =
  getApps().length > 0 ? getApps()[0]! : initializeApp(firebaseConfig)

export const functions: Functions = getFunctions(firebaseApp, FUNCTIONS_REGION)

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string
    __COVERBOOK_FUNCTIONS_EMULATOR__?: boolean
  }
}

if (
  USE_EMULATORS &&
  typeof window !== 'undefined' &&
  !window.__COVERBOOK_FUNCTIONS_EMULATOR__
) {
  window.__COVERBOOK_FUNCTIONS_EMULATOR__ = true
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}

// ---------------------------------------------------------------------------
// App Check
// ---------------------------------------------------------------------------

let appCheckInstance: AppCheck | null = null

/**
 * App Check attests that a request comes from our real app, which is what
 * makes the public booking callables resistant to scripted abuse.
 *
 * It SUPPLEMENTS authentication, authorisation, Security Rules, validation and
 * rate limiting — it does not replace any of them. Every public callable
 * behind it is independently rate limited and independently validated, so
 * turning App Check off during a staged rollout weakens one layer rather than
 * opening the door.
 *
 * Locally, `VITE_APPCHECK_DEBUG=true` requests a debug token, which must be
 * registered once in the Firebase console (see README, "App Check setup").
 */
export function initialiseAppCheck(): AppCheck | null {
  if (typeof window === 'undefined') return null
  if (appCheckInstance) return appCheckInstance

  const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY
  if (import.meta.env.VITE_APPCHECK_DEBUG === 'true') {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true
  }

  if (!siteKey) {
    // No site key configured: App Check simply is not active on the client.
    // The backend decides whether to ENFORCE, so this cannot silently weaken
    // production — deployed functions reject unattested calls.
    return null
  }

  try {
    // reCAPTCHA v3 rather than Enterprise: for a booking widget the score-based
    // signal is the same, v3 has no per-assessment cost, and the provider is
    // the only thing that differs — App Check enforcement, the token refresh
    // and every server-side check are identical either way. Swapping back is a
    // one-line change plus a different key in the console.
    appCheckInstance = initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })
    return appCheckInstance
  } catch (error) {
    console.warn('App Check could not be initialised', error)
    return null
  }
}

export const APP_BASE_URL =
  import.meta.env.VITE_APP_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '')
export const WIDGET_BASE_URL = import.meta.env.VITE_WIDGET_BASE_URL || APP_BASE_URL
